import { useEffect, useState } from 'react';
import PeerReviewDocumentViewer from './PeerReviewDocumentViewer';
import StructuredFormPeerReviewViewer from './StructuredFormPeerReviewViewer';


const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders(extra = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function readJson(response, label) {
  const text = await response.text();
  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!response.ok) {
    const detail = typeof data === 'string'
      ? data
      : data?.message || data?.hint || text;
    throw new Error(`${label} failed (${response.status}): ${detail}`);
  }

  return data;
}

async function rpc(name, body, label) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/${name}`,
    {
      method: 'POST',
      headers: apiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body)
    }
  );

  const data = await readJson(response, label);
  return Array.isArray(data) && data.length === 1 ? data[0] : data;
}

async function fetchConfig(courseAssignmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/peer_review_configs?course_assignment_id=eq.${courseAssignmentId}&select=*&limit=1`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading Peer Review settings');
  return rows?.[0] || null;
}

async function fetchAllocation(courseAssignmentId, reviewerEnrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/peer_review_allocations?peer_review_course_assignment_id=eq.${courseAssignmentId}&reviewer_enrolment_id=eq.${reviewerEnrolmentId}&select=*&limit=1`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading Peer Review allocation');
  return rows?.[0] || null;
}

async function fetchReview(allocationId) {
  if (!allocationId) return null;

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/peer_review_submissions?allocation_id=eq.${allocationId}&select=*&limit=1`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading Peer Review draft');
  return rows?.[0] || null;
}

async function fetchComments(reviewId) {
  if (!reviewId) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/peer_review_comments?peer_review_submission_id=eq.${reviewId}&select=*&order=page_number.asc,created_at.asc`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading Peer Review comments');
  return Array.isArray(rows) ? rows : [];
}

async function fetchReviewee(enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?id=eq.${enrolmentId}&select=id,students(student_code,english_name,chinese_name)&limit=1`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading reviewed student'
  );

  return rows?.[0] || null;
}

async function fetchAssignmentGroupName(
  courseAssignmentId,
  groupNumber
) {
  if (
    !courseAssignmentId ||
    !groupNumber
  ) {
    return '';
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_labels?course_assignment_id=eq.${encodeURIComponent(
      courseAssignmentId
    )}&group_number=eq.${Number(
      groupNumber
    )}&select=name&limit=1`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading reviewed group name'
  );

  return (
    String(rows?.[0]?.name || '').trim() ||
    `Group ${groupNumber}`
  );
}

function studentName(enrolment) {
  const student = enrolment?.students;
  return (
    student?.english_name ||
    student?.chinese_name ||
    student?.student_code ||
    'Student'
  );
}

export default function StudentPeerReview({
  courseAssignment,
  enrolment,
  status
}) {
  const [config, setConfig] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [review, setReview] = useState(null);
  const [comments, setComments] = useState([]);
  const [reviewee, setReviewee] =
    useState(null);

  const [
    reviewedGroupName,
    setReviewedGroupName
  ] = useState('');

  const [overallComment, setOverallComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingOverall, setSavingOverall] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      setSuccess('');
      setReviewedGroupName('');

      try {
        const loadedConfig = await fetchConfig(courseAssignment.id);
        if (cancelled) return;

        setConfig(loadedConfig);

        if (!loadedConfig || loadedConfig.allocation_status !== 'finalized') {
          setAllocation(null);
          setReview(null);
          setComments([]);
          return;
        }

        const loadedAllocation = await fetchAllocation(
          courseAssignment.id,
          enrolment.id
        );

        if (cancelled) return;
        setAllocation(loadedAllocation);

        if (!loadedAllocation) {
          setReview(null);
          setComments([]);
          return;
        }

        if (
          loadedAllocation
            .target_group_number
        ) {
          setReviewee(null);

          if (!loadedConfig.anonymous) {
            const groupName =
              await fetchAssignmentGroupName(
                loadedConfig
                  .target_course_assignment_id,
                loadedAllocation
                  .target_group_number
              );

            if (!cancelled) {
              setReviewedGroupName(
                groupName
              );
            }
          }
        } else if (
          !loadedConfig.anonymous
        ) {
          const loadedReviewee =
            await fetchReviewee(
              loadedAllocation
                .reviewee_enrolment_id
            );

          if (!cancelled) {
            setReviewee(
              loadedReviewee
            );
          }
        } else {
          setReviewee(null);
        }

        let loadedReview = await fetchReview(loadedAllocation.id);

        // Do not create a draft before the Peer Review assignment itself opens.
        if (!loadedReview && status === 'open') {
          loadedReview = await rpc(
            'peer_review_get_or_create_review',
            {
              p_peer_review_course_assignment_id: courseAssignment.id,
              p_reviewer_enrolment_id: enrolment.id
            },
            'Creating Peer Review draft'
          );
        }

        if (cancelled) return;

        setReview(loadedReview || null);
        setOverallComment(loadedReview?.overall_comment || '');

        const loadedComments = loadedReview
          ? await fetchComments(loadedReview.id)
          : [];

        if (!cancelled) setComments(loadedComments);
      } catch (err) {
        console.error('Peer Review load failed:', err);
        if (!cancelled) {
          setError(err?.message || 'Could not load Peer Review.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [courseAssignment.id, enrolment.id, status]);

  const editable = status === 'open' && review && !review.submitted;

  async function ensureReview() {
    if (review) return review;
    if (!allocation || status !== 'open') return null;

    const created = await rpc(
      'peer_review_get_or_create_review',
      {
        p_peer_review_course_assignment_id: courseAssignment.id,
        p_reviewer_enrolment_id: enrolment.id
      },
      'Creating Peer Review draft'
    );

    setReview(created);
    setOverallComment(created?.overall_comment || '');
    return created;
  }

  async function handleAddComment(comment) {
    setError('');
    setSuccess('');

    const currentReview = await ensureReview();
    if (!currentReview) {
      throw new Error('The Peer Review is not currently editable.');
    }

    try {
      const created = await rpc(
        'peer_review_add_comment',
        {
          p_review_id: currentReview.id,
          p_reviewer_enrolment_id: enrolment.id,
          p_page_number: comment.pageNumber,
          p_selected_text: comment.selectedText,
          p_anchor_data: comment.anchorData,
          p_comment_text: comment.commentText
        },
        'Adding inline comment'
      );

      setComments(prev => [...prev, created]);
    } catch (err) {
      setError(err?.message || 'Could not add comment.');
      throw err;
    }
  }

  async function handleUpdateComment(commentId, text) {
    setError('');
    setSuccess('');

    try {
      const updated = await rpc(
        'peer_review_update_comment',
        {
          p_comment_id: commentId,
          p_reviewer_enrolment_id: enrolment.id,
          p_comment_text: text
        },
        'Updating inline comment'
      );

      setComments(prev =>
        prev.map(comment =>
          comment.id === commentId ? updated : comment
        )
      );
    } catch (err) {
      setError(err?.message || 'Could not update comment.');
      throw err;
    }
  }

  async function handleDeleteComment(commentId) {
    setError('');
    setSuccess('');

    try {
      await rpc(
        'peer_review_delete_comment',
        {
          p_comment_id: commentId,
          p_reviewer_enrolment_id: enrolment.id
        },
        'Deleting inline comment'
      );

      setComments(prev => prev.filter(comment => comment.id !== commentId));
    } catch (err) {
      setError(err?.message || 'Could not delete comment.');
      throw err;
    }
  }

  async function saveOverall(showSuccess = true) {
    const currentReview = await ensureReview();
    if (!currentReview) return null;

    setSavingOverall(true);
    setError('');
    if (showSuccess) setSuccess('');

    try {
      const updated = await rpc(
        'peer_review_save_overall_comment',
        {
          p_review_id: currentReview.id,
          p_reviewer_enrolment_id: enrolment.id,
          p_overall_comment: overallComment
        },
        'Saving overall comment'
      );

      setReview(updated);
      setOverallComment(updated.overall_comment || '');
      if (showSuccess) setSuccess('Peer Review draft saved.');
      return updated;
    } catch (err) {
      setError(err?.message || 'Could not save Peer Review draft.');
      throw err;
    } finally {
      setSavingOverall(false);
    }
  }

  async function handleSubmit() {
    const currentReview = await ensureReview();
    if (!currentReview) return;

    if (!window.confirm(
      'Submit this Peer Review? After submission, you will not be able to edit or delete your comments unless an instructor unsubmits the review.'
    )) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await saveOverall(false);

      const submitted = await rpc(
        'peer_review_submit',
        {
          p_review_id: currentReview.id,
          p_reviewer_enrolment_id: enrolment.id
        },
        'Submitting Peer Review'
      );

      setReview(submitted);
      setSuccess('Peer Review submitted.');
    } catch (err) {
      setError(err?.message || 'Could not submit Peer Review.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div style={styles.stateBox}>Loading Peer Review...</div>;
  }

  if (error && !allocation) {
    return <div style={styles.error}>{error}</div>;
  }

  if (!config || config.allocation_status !== 'finalized') {
    return (
      <div style={styles.stateBox}>
        Peer Review allocations have not been released yet.
      </div>
    );
  }

  if (!allocation) {
    return (
      <div style={styles.stateBox}>
        No review has been assigned to you.
      </div>
    );
  }

  if (!review && status !== 'open') {
    return (
      <div style={styles.stateBox}>
        {status === 'not_open'
          ? 'This Peer Review is not open yet.'
          : 'The Peer Review submission window is closed.'}
      </div>
    );
  }

  const reviewedStudentLabel =
    allocation?.target_group_number
      ? (
          config.anonymous
            ? 'Anonymous group submission'
            : (
                reviewedGroupName ||
                `Group ${
                  allocation
                    .target_group_number
                }`
              )
        )
      : (
          config.anonymous
            ? 'Anonymous submission'
            : studentName(reviewee)
        );

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.eyebrow}>Reviewing</div>
          <div style={styles.reviewedStudent}>{reviewedStudentLabel}</div>
        </div>

        <div
          style={review?.submitted
            ? styles.submittedPill
            : styles.draftPill}
        >
          {review?.submitted ? 'Submitted' : 'Draft'}
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {review?.submitted && (
        <div style={styles.lockedNotice}>
          This Peer Review is submitted and locked. Only an instructor can
          return it to draft status.
        </div>
      )}

      {!review?.submitted && status !== 'open' && (
        <div style={styles.lockedNotice}>
          The Peer Review submission window is not currently open.
        </div>
      )}

            {config?.target_structured_instance_id ? (
        <StructuredFormPeerReviewViewer
          targetSubmissionId={allocation.target_submission_id}
          comments={comments}
          editable={editable}
          onAddComment={handleAddComment}
          onUpdateComment={handleUpdateComment}
          onDeleteComment={handleDeleteComment}
        />
      ) : (
        <PeerReviewDocumentViewer
          allocationId={allocation.id}
          comments={comments}
          editable={editable}
          onAddComment={handleAddComment}
          onUpdateComment={handleUpdateComment}
          onDeleteComment={handleDeleteComment}
        />
      )}


      <div style={styles.overallBox}>
        <label style={styles.overallLabel}>Overall comments</label>

        {editable ? (
          <textarea
            style={styles.overallTextarea}
            rows={8}
            value={overallComment}
            onChange={event => setOverallComment(event.target.value)}
            placeholder="Add your overall comments on the submission..."
          />
        ) : (
          <div style={styles.overallReadOnly}>
            {overallComment.trim() || 'No overall comment.'}
          </div>
        )}

        {editable && (
          <div style={styles.actions}>
            <button
              type="button"
              style={styles.secondaryBtn}
              disabled={savingOverall || submitting}
              onClick={() => saveOverall(true)}
            >
              {savingOverall ? 'Saving...' : 'Save Draft'}
            </button>

            <button
              type="button"
              style={styles.primaryBtn}
              disabled={savingOverall || submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'Submitting...' : 'Submit Peer Review'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    marginTop: '8px'
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '10px'
  },
  eyebrow: {
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#7a8894',
    fontWeight: '800'
  },
  reviewedStudent: {
    marginTop: '2px',
    fontSize: '16px',
    fontWeight: '800',
    color: '#1f2933'
  },
  draftPill: {
    borderRadius: '999px',
    background: '#fff4d7',
    color: '#8a5b00',
    padding: '4px 9px',
    fontSize: '11px',
    fontWeight: '800'
  },
  submittedPill: {
    borderRadius: '999px',
    background: '#e7f6ed',
    color: '#276749',
    padding: '4px 9px',
    fontSize: '11px',
    fontWeight: '800'
  },
  stateBox: {
    padding: '16px',
    border: '1px solid #dce3e8',
    borderRadius: '8px',
    color: '#66788a',
    background: '#fafcfd'
  },
  lockedNotice: {
    padding: '9px 11px',
    border: '1px solid #d9e1e7',
    borderRadius: '7px',
    background: '#f7f9fa',
    color: '#66788a',
    fontSize: '12px',
    marginBottom: '10px'
  },
  overallBox: {
    marginTop: '16px',
    paddingTop: '14px',
    borderTop: '1px solid #dce3e8'
  },
  overallLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '800',
    color: '#1f2933',
    marginBottom: '7px'
  },
  overallTextarea: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #cbd5dc',
    borderRadius: '8px',
    padding: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    lineHeight: 1.5,
    resize: 'vertical'
  },
  overallReadOnly: {
    whiteSpace: 'pre-wrap',
    border: '1px solid #e0e6ea',
    borderRadius: '8px',
    padding: '11px',
    background: '#fafbfc',
    color: '#334e68',
    lineHeight: 1.55
  },
  actions: {
    marginTop: '10px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    flexWrap: 'wrap'
  },
  primaryBtn: {
    border: '1px solid #215b7d',
    background: '#215b7d',
    color: 'white',
    borderRadius: '7px',
    padding: '8px 12px',
    fontWeight: '800',
    cursor: 'pointer'
  },
  secondaryBtn: {
    border: '1px solid #cbd5dc',
    background: 'white',
    color: '#334e68',
    borderRadius: '7px',
    padding: '8px 12px',
    fontWeight: '800',
    cursor: 'pointer'
  },
  error: {
    margin: '8px 0',
    padding: '9px 11px',
    background: '#fff4f4',
    border: '1px solid #efb7b7',
    borderRadius: '7px',
    color: '#912d2d',
    fontSize: '12px'
  },
  success: {
    margin: '8px 0',
    padding: '9px 11px',
    background: '#f0faf4',
    border: '1px solid #b9dec7',
    borderRadius: '7px',
    color: '#276749',
    fontSize: '12px'
  }
};